import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { createPortal } from "react-dom";
import { applyAppleFont, clearAppleFontClass, getStoredAppleFont } from "./appleFont";
import { applyListDisplayClasses, getStoredListDisplay } from "./applePosterLayout";
import { APPLE_FLOATING_NAV_OPEN, APPLE_MOBILE_NAV_CLOSE, APPLE_MOBILE_NAV_OPEN, APPLE_MOBILE_NAV_TOGGLE, publishAppleMobileNavState } from "./appleMobileNav";
import { CURATOR_TV_SITE_NAME } from "./components/appleTvLinks";
import {
  appleAccountPath,
  appleHomePath,
  appleListPath,
  appleReelsPath,
  appleRoute,
  appleSearchPath,
  appleTvHomePath,
  toAppleRelativePath,
} from "./appleTvHost";
import AppleTvMobileTopBar from "./AppleTvMobileTopBar";
import {
  HOME_SHELF_NAV_ITEMS,
  TV_HOME_SHELF_NAV_ITEMS,
  RECENT_LISTS_UPDATED_EVENT,
  areRecentListVisitsEqual,
  getSidebarRecentListVisits,
  homeShelfListPath,
  recentListEntryPath,
} from "./homeShelfNav";
import useCurrentCuratorProfilePath from "./useCurrentCuratorProfilePath";

const NAV_ITEMS = [
  { to: appleSearchPath(), label: "Search", icon: "search" },
  { to: appleHomePath(), label: "Home", icon: "home" },
  { to: appleRoute("/library"), label: "Library", icon: "library" },
  { to: "/developer/api", label: "API", icon: "code" },
  { to: "/guide/live-guide/index.html", label: "Print", icon: "print", external: true },
  { to: "/", label: "Watchlist", icon: "bookmark", hideOnPrimaryHost: true },
];

// How many Home shelf / Recent links to show before the "More" toggle.
const SIDE_NAV_PREVIEW_COUNT = 5;

const CONDENSED_ITEMS = [
  { to: appleSearchPath(), label: "Search", icon: "search" },
  { to: appleHomePath(), label: "Home", icon: "home" },
  { to: appleReelsPath(), label: "Reels", icon: "reels" },
  { to: appleListPath("movies"), label: "Movies", icon: "film" },
  { to: appleTvHomePath(), label: "TV Shows", icon: "tv" },
  { to: appleRoute("/library"), label: "Library", icon: "library" },
  { to: "/newsroom", label: "Newsroom", icon: "newspaper" },
];

function SideIcon({ type }) {
  const stroke = 1.45;
  if (type === "search") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="10.5" cy="10.5" r="5.5" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M14.8 14.8 18.8 18.8" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "film") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="4.2" y="5.4" width="15.6" height="13.2" rx="2.2" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M8 5.4v13.2M16 5.4v13.2" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M4.2 8.8h3.8M4.2 12h3.8M4.2 15.2h3.8M16 8.8h3.8M16 12h3.8M16 15.2h3.8" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "tv") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="4.2" y="5.8" width="15.6" height="10.8" rx="2.4" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M9.2 19.2h5.6" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
        <path d="M12 16.6v2.6" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "reels") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="4" y="5.4" width="16" height="13.2" rx="3" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M8.4 5.6 10.8 9M13.2 5.4 15.6 8.9" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
        <path d="M10.6 11.4v3.6l3.1-1.8-3.1-1.8z" fill="currentColor" stroke="currentColor" strokeWidth={stroke * 0.6} strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "clock") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="6.8" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M12 8.6v3.5l2.4 1.6" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "download") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 6.2v7.4m0 0 2.6-2.6M12 13.6l-2.6-2.6M6.4 17.4h11.2" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "library") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="4.4" y="4.4" width="6.6" height="6.6" rx="1.8" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <rect x="13" y="4.4" width="6.6" height="6.6" rx="1.8" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <rect x="4.4" y="13" width="6.6" height="6.6" rx="1.8" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <rect x="13" y="13" width="6.6" height="6.6" rx="1.8" fill="none" stroke="currentColor" strokeWidth={stroke} />
      </svg>
    );
  }
  if (type === "map") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M5 6.4 10 4.6l4 1.5 5-1.7v13.2l-5 1.8-4-1.5-5 1.7V6.4z" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinejoin="round" />
        <path d="M10 4.8v13.1M14 6.2v13" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
        <path d="M7.2 10.2c1.1-.8 2-.7 3 .1 1.2.9 2.3.9 3.5.1 1.1-.8 2.1-.9 3-.3" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "home") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M12 5.1 5.2 10.6V19h13.6v-8.4L12 5.1z"
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinejoin="round"
        />
        <path d="M10.4 19v-4.6h3.2V19" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "bookmark") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M7.2 5.8h9.6a.9.9 0 0 1 .9.9v10.2l-5.7-3-5.7 3V6.7a.9.9 0 0 1 .9-.9z" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "print") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M7.2 9.4V6.4h9.6v3" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
        <rect x="5.6" y="9.4" width="12.8" height="5.4" rx="1.4" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M7.2 14.8V18h9.6v-3.2" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "code") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M8.6 8.5 5.2 12l3.4 3.5M15.4 8.5l3.4 3.5-3.4 3.5M13.8 6.8l-3 10.4" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "newspaper") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M6.6 8.4H4.8A1.3 1.3 0 0 0 3.5 9.7v7a1.9 1.9 0 0 0 1.9 1.9" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
        <rect x="6.6" y="4.6" width="13.9" height="14.8" rx="2" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M9.6 8.2h7.4M9.6 10.5h7.4" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
        <rect x="9.6" y="12.7" width="3.4" height="3.4" rx="0.7" fill="currentColor" />
        <path d="M14.5 13.6h2.5M14.5 15.9h2.5" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "user") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="8.8" r="3" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M6.2 18.6c1.1-2.6 3.1-3.9 5.8-3.9s4.7 1.3 5.8 3.9" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "sidebar" || type === "toggle-close" || type === "toggle-open") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="4" y="5.4" width="16" height="13.2" rx="2.4" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M9.2 5.4v13.2" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M5 11.2 11.2 6.2a.8.8 0 0 1 1 0l6.2 5v6.8a.9.9 0 0 1-.9.9h-3.8v-4.6a.9.9 0 0 0-.9-.9h-3a.9.9 0 0 0-.9.9v4.6H5.9a.9.9 0 0 1-.9-.9v-6.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinejoin="round"
      />
    </svg>
  );
}

function getUserDisplayName(user) {
  const first = String(user?.given_name || "").trim();
  const last = String(user?.family_name || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  const full = String(user?.name || "").trim();
  if (full) return full;
  const nick = String(user?.nickname || "").trim();
  if (nick) return nick;
  const email = String(user?.email || "").trim();
  if (email) return email.split("@")[0];
  return "Account";
}

function getInitials(value) {
  const cleaned = String(value || "").trim();
  if (!cleaned) return "A";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (!parts.length) return "A";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
}

const SIDEBAR_ANIM_MS = 220;

function isHomeShelfNavActive(key, pathname, isTvHomeSection) {
  const listPath = homeShelfListPath(key);
  if (pathname === listPath) return true;
  if (key === "theaters" && pathname === appleListPath("amc-lincoln-13")) return true;
  if (isTvHomeSection && pathname === appleTvHomePath() && key === "tv-home") return true;
  return false;
}

const AppleTvSideNav = ({ suppressMobileTriggers = false, floating = false, mobileChromeTitle = CURATOR_TV_SITE_NAME }) => {
  const { isAuthenticated, loginWithRedirect, user } = useAuth0();
  const { pathname } = useLocation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 740 : false));
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  // "Floating" mode (Reels): the desktop sidebar hides behind a single button
  // so the surface underneath is fullscreen; opening reveals it as an overlay.
  const [floatingOpen, setFloatingOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = window.localStorage.getItem("appleSidebarExpanded");
    if (saved === "true") return true;
    if (saved === "false") return false;
    return window.innerWidth >= 1200;
  });
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [recentListVisits, setRecentListVisits] = useState(() => getSidebarRecentListVisits());
  const [homeShelvesExpanded, setHomeShelvesExpanded] = useState(false);
  const [recentExpanded, setRecentExpanded] = useState(false);
  const collapseTimerRef = useRef(null);

  // Apply the saved appearance theme on every Apple page (the side nav is
  // rendered by all of them); clear the body class when leaving Apple routes.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const refreshRecentLists = () => {
      setRecentListVisits((prev) => {
        const next = getSidebarRecentListVisits();
        return areRecentListVisitsEqual(prev, next) ? prev : next;
      });
    };
    refreshRecentLists();
    window.addEventListener(RECENT_LISTS_UPDATED_EVENT, refreshRecentLists);
    window.addEventListener("storage", refreshRecentLists);
    window.addEventListener("focus", refreshRecentLists);
    return () => {
      window.removeEventListener(RECENT_LISTS_UPDATED_EVENT, refreshRecentLists);
      window.removeEventListener("storage", refreshRecentLists);
      window.removeEventListener("focus", refreshRecentLists);
    };
  }, []);

  useEffect(() => {
    // Theme is owned by App (survives SideNav remounts / route changes).
    applyAppleFont(getStoredAppleFont());
    applyListDisplayClasses(getStoredListDisplay());
    return () => {
      clearAppleFontClass();
      applyListDisplayClasses("grid");
    };
  }, []);

  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) {
        window.clearTimeout(collapseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => {
      const mobile = window.innerWidth <= 740;
      setIsMobile(mobile);
      if (!mobile) setIsMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const openFloating = () => setFloatingOpen(true);
    window.addEventListener(APPLE_FLOATING_NAV_OPEN, openFloating);
    return () => window.removeEventListener(APPLE_FLOATING_NAV_OPEN, openFloating);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const openDrawer = () => {
      setIsMobileOpen(true);
      setIsExpanded(true);
    };
    const closeDrawer = () => setIsMobileOpen(false);
    const toggleDrawer = () => setIsMobileOpen((prev) => !prev);
    window.addEventListener(APPLE_MOBILE_NAV_OPEN, openDrawer);
    window.addEventListener(APPLE_MOBILE_NAV_CLOSE, closeDrawer);
    window.addEventListener(APPLE_MOBILE_NAV_TOGGLE, toggleDrawer);
    return () => {
      window.removeEventListener(APPLE_MOBILE_NAV_OPEN, openDrawer);
      window.removeEventListener(APPLE_MOBILE_NAV_CLOSE, closeDrawer);
      window.removeEventListener(APPLE_MOBILE_NAV_TOGGLE, toggleDrawer);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      publishAppleMobileNavState(false);
      return;
    }
    publishAppleMobileNavState(isMobileOpen);
  }, [isMobile, isMobileOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("appleSidebarExpanded", isExpanded ? "true" : "false");
    // In floating mode the sidebar is an overlay, so never reserve layout width.
    const shouldExpandBody = !isMobile && !floating && (isExpanded || isCollapsing);
    document.body.classList.toggle("apple-side-expanded", shouldExpandBody);
    return () => {
      document.body.classList.remove("apple-side-expanded");
    };
  }, [isExpanded, isCollapsing, isMobile, floating]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!(isMobile && isMobileOpen)) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [isMobile, isMobileOpen]);

  const closeIfMobile = () => {
    if (isMobile) setIsMobileOpen(false);
    if (floating) setFloatingOpen(false);
  };

  const renderCuratorItem = (item) => {
    if (item.external) {
      return (
        <a
          key={item.to}
          href={item.to}
          className="apple-clone__side-link"
          aria-label={item.label}
          title={item.label}
          onClick={closeIfMobile}
        >
          <SideIcon type={item.icon} />
          <span className="apple-clone__side-link-label">{item.label}</span>
        </a>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === appleHomePath()}
        className={({ isActive }) => `apple-clone__side-link ${isActive ? "is-active" : ""}`}
        title={item.label}
        aria-label={item.label}
        onClick={closeIfMobile}
      >
        <SideIcon type={item.icon} />
        <span className="apple-clone__side-link-label">{item.label}</span>
      </NavLink>
    );
  };

  const floatingActive = floating && !isMobile;
  const showExpanded = isMobile ? true : (floatingActive ? floatingOpen : isExpanded);
  const headerVisible = showExpanded && !isCollapsing;
  const headerHiding = !isMobile && isCollapsing;
  const headerHidden = !isMobile && !showExpanded && !isCollapsing;
  const toggleVisible = !isMobile && (!showExpanded || isCollapsing);
  const primaryItems = CONDENSED_ITEMS;
  const showShelvesSection = showExpanded;
  const isTvHomeContext = pathname === appleTvHomePath() || pathname.startsWith(appleListPath("tv"));
  const homeShelfItems = isTvHomeContext ? TV_HOME_SHELF_NAV_ITEMS : HOME_SHELF_NAV_ITEMS;

  const renderHomeShelfLink = (item) => {
    const to = item.key === "tv-home"
      ? appleTvHomePath()
      : (item.to || homeShelfListPath(item.key));
    const isActive = isHomeShelfNavActive(item.key, pathname, isTvHomeContext);
    return (
      <NavLink
        key={item.key}
        to={to}
        className={`apple-clone__side-shelf-link${isActive ? " is-active" : ""}`}
        title={item.label}
        aria-label={item.label}
        onClick={closeIfMobile}
      >
        {item.label}
      </NavLink>
    );
  };

  const renderRecentLink = (item) => {
    const to = recentListEntryPath(item);
    return (
      <NavLink
        key={item.key}
        to={to}
        isActive={(_, location) => `${location.pathname}${location.search}` === to}
        className={({ isActive }) => `apple-clone__side-shelf-link${isActive ? " is-active" : ""}`}
        title={item.label}
        aria-label={item.label}
        onClick={closeIfMobile}
      >
        {item.label}
      </NavLink>
    );
  };
  const handleToggleClick = () => {
    if (isMobile) {
      setIsMobileOpen(false);
      return;
    }
    if (floatingActive) {
      setFloatingOpen(false);
      return;
    }
    if (isExpanded) {
      setIsCollapsing(true);
      setIsExpanded(false);
      if (collapseTimerRef.current) {
        window.clearTimeout(collapseTimerRef.current);
      }
      collapseTimerRef.current = window.setTimeout(() => {
        setIsCollapsing(false);
        collapseTimerRef.current = null;
      }, SIDEBAR_ANIM_MS);
      return;
    }
    setIsCollapsing(false);
    setIsExpanded(true);
  };

  const profileDisplayName = getUserDisplayName(user);
  const profileInitials = getInitials(profileDisplayName);
  const profilePicture = String(user?.picture || "").trim();
  const hasProfileAvatar = Boolean(profilePicture) && !avatarLoadFailed;
  const profilePath = useCurrentCuratorProfilePath({
    isAuthenticated,
    user,
    fallbackPath: "/apple/account",
  });
  const accountPath = appleAccountPath();
  // Prefer live hook path when it already resolved to films; else smart account path.
  const accountTo = String(profilePath || "").includes("/films") ? profilePath : accountPath;
  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [profilePicture]);

  const sidebar = (
    <aside
      className={`apple-clone__side-nav ${showExpanded ? "is-expanded" : "is-collapsed"}${isCollapsing ? " is-collapsing" : ""}${isMobile ? ` is-mobile-drawer${isMobileOpen ? " is-mobile-drawer-open" : ""}` : ""}${floatingActive ? ` is-floating${floatingOpen ? " is-floating-open" : ""}` : ""}`}
      aria-label="Curator TV sections"
      aria-hidden={isMobile && !isMobileOpen ? "true" : undefined}
    >
      <div className="apple-clone__side-nav-inner">
        {!isMobile ? (
          <div className="apple-clone__side-top">
            <div
              className={`apple-clone__side-header${headerVisible ? " is-visible" : ""}${headerHiding ? " is-hiding" : ""}${headerHidden ? " is-hidden" : ""}`}
            >
              <div className="apple-clone__side-header-title">{CURATOR_TV_SITE_NAME}</div>
              <button
                type="button"
                className="apple-clone__side-header-toggle"
                onClick={handleToggleClick}
                aria-label="Collapse sidebar"
                title="Collapse sidebar"
              >
                <SideIcon type="toggle-close" />
              </button>
            </div>
            <button
              type="button"
              className={`apple-clone__side-link apple-clone__side-link--button apple-clone__side-toggle${toggleVisible ? " is-visible" : " is-hidden"}`}
              onClick={handleToggleClick}
              aria-label="Expand sidebar"
              title="Expand sidebar"
              tabIndex={toggleVisible ? 0 : -1}
            >
              <SideIcon type="toggle-open" />
              <span className="apple-clone__side-link-label">Expand</span>
            </button>
          </div>
        ) : null}

        <div className="apple-clone__side-nav-top">
          <div className="apple-clone__side-group">
            {primaryItems.map(renderCuratorItem)}
          </div>
        </div>

        {showShelvesSection ? (
          <div className="apple-clone__side-scroll">
            <div className="apple-clone__side-section-header">
              <div className="apple-clone__side-section-title">{isTvHomeContext ? "TV Home" : "Home"}</div>
              {homeShelfItems.length > SIDE_NAV_PREVIEW_COUNT ? (
                <button
                  type="button"
                  className="apple-clone__side-section-toggle"
                  onClick={() => setHomeShelvesExpanded((v) => !v)}
                  aria-expanded={homeShelvesExpanded}
                >
                  {homeShelvesExpanded ? "Less" : "More"}
                </button>
              ) : null}
            </div>
            <div className="apple-clone__side-shelf-links">
              {(homeShelvesExpanded ? homeShelfItems : homeShelfItems.slice(0, SIDE_NAV_PREVIEW_COUNT)).map(renderHomeShelfLink)}
            </div>
            {recentListVisits.length ? (
              <>
                <div className="apple-clone__side-list-divider" aria-hidden="true" />
                <div className="apple-clone__side-section-header">
                  <div className="apple-clone__side-section-title">Recent</div>
                  {recentListVisits.length > SIDE_NAV_PREVIEW_COUNT ? (
                    <button
                      type="button"
                      className="apple-clone__side-section-toggle"
                      onClick={() => setRecentExpanded((v) => !v)}
                      aria-expanded={recentExpanded}
                    >
                      {recentExpanded ? "Less" : "More"}
                    </button>
                  ) : null}
                </div>
                <div className="apple-clone__side-shelf-links">
                  {(recentExpanded ? recentListVisits : recentListVisits.slice(0, SIDE_NAV_PREVIEW_COUNT)).map(renderRecentLink)}
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <div className="apple-clone__side-spacer" />
        )}

        <div className="apple-clone__side-footer">
          {isAuthenticated ? (
            <NavLink
              to={accountTo}
              className={({ isActive }) => `apple-clone__side-link ${isActive ? "is-active" : ""}`}
              aria-label="Account"
              title="Account"
              onClick={closeIfMobile}
            >
              <span className="apple-clone__side-avatar" aria-hidden="true">
                {hasProfileAvatar ? (
                  <img
                    src={profilePicture}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarLoadFailed(true)}
                  />
                ) : (
                  <span className="apple-clone__side-avatar-fallback">{profileInitials}</span>
                )}
              </span>
              <span className="apple-clone__side-link-label apple-clone__side-account-name">{profileDisplayName}</span>
            </NavLink>
          ) : (
            <button
              type="button"
              className="apple-clone__side-link apple-clone__side-link--button"
              onClick={() => {
                loginWithRedirect();
                closeIfMobile();
              }}
              aria-label="Account"
              title="Account"
            >
              <SideIcon type="user" />
              <span className="apple-clone__side-link-label">Account</span>
            </button>
          )}
        </div>
      </div>
    </aside>
  );

  const mobileTrigger = isMobile && !isMobileOpen && !suppressMobileTriggers ? (
    <>
      <button
        type="button"
        className="apple-clone__mobile-nav-trigger apple-clone__mobile-nav-trigger--menu"
        onClick={() => {
          setIsMobileOpen(true);
          setIsExpanded(true);
        }}
        aria-label="Open sidebar"
        title="Open sidebar"
      >
        <SideIcon type="bookmark" />
      </button>
      <button
        type="button"
        className="apple-clone__mobile-nav-trigger apple-clone__mobile-nav-trigger--search"
        onClick={() => {
          window.location.assign(appleSearchPath());
        }}
        aria-label="Open search"
        title="Open search"
      >
        <SideIcon type="search" />
      </button>
    </>
  ) : null;

  const floatingTrigger = floatingActive && !floatingOpen ? (
    <button
      type="button"
      className="apple-clone__floating-nav-trigger"
      onClick={() => setFloatingOpen(true)}
      aria-label="Open menu"
      title="Menu"
    >
      <SideIcon type="sidebar" />
    </button>
  ) : null;

  const floatingBackdrop = floatingActive && floatingOpen ? (
    <button
      type="button"
      className="apple-clone__floating-nav-backdrop"
      aria-label="Close menu"
      onClick={() => setFloatingOpen(false)}
    />
  ) : null;

  if (typeof document === "undefined") return null;
  return createPortal(
    <>
      <AppleTvMobileTopBar title={mobileChromeTitle} />
      {mobileTrigger}
      {floatingTrigger}
      {floatingBackdrop}
      {sidebar}
    </>,
    document.body,
  );
};

export default AppleTvSideNav;
